import { DecimalFormatPipePipe, ImpactDataFormateEnum } from '@piqet-angular/shared';
import {
  ChartReportContribution,
  ChartReportPefValue,
  ChartReportScenario,
  GenerateChartReportData,
} from './chart-report.model';

/**
 * Adapters from the `GenerateChartReport` response to the view models the
 * shared chart components already consume:
 *
 *   - `piqet-angular-bar-chart`  <- `[{ name, componentName, impactValues, percentage }]`
 *   - `piqet-angular-radar-chart` <- `[{ scenarioname, indicators, indicatorImageName, totals }]`
 *   - `piqet-angular-gauge-chart` <- `[{ componentId, componentName, value, formatedValue }]`
 *
 * Keeping those renderers untouched is what makes the new screen look identical
 * to the old one — only where the numbers come from changes. Each number is now
 * read from its own `{ scenarioId, indicatorId, bucketKey }` record instead of
 * being split out of a `;`-joined string by position.
 */

const decimalFormat = new DecimalFormatPipePipe();

/**
 * Renders a raw engine value the way the legacy endpoints pre-formatted it
 * (3 SF / 3 dp / scientific), so the chart tooltips and the table view read
 * exactly as they do today. `parseNumericValue` in the bar chart strips the
 * thousand separators this can emit, so the chart still plots the true value.
 */
function formatImpactValue(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return decimalFormat.transform(value, ImpactDataFormateEnum.Default);
}

function formatPercentage(percentage: number | undefined | null): string {
  if (percentage === undefined || percentage === null || isNaN(percentage)) return '0';
  return String(percentage);
}

/** Scenario column order — every `;`-joined projection below follows it. */
function scenarioOrder(scenarios: ChartReportScenario[]): number[] {
  return (scenarios ?? []).map(s => s.scenarioId);
}

/**
 * Picks the contribution block to chart. The glance screen requests a single
 * indicator, so there is normally exactly one; `indicatorId` disambiguates if
 * the backend ever returns more.
 */
export function pickContribution(
  data: GenerateChartReportData,
  indicatorId?: number
): ChartReportContribution | null {
  const contributions = data?.contributions ?? [];
  if (!contributions.length) return null;
  return contributions.find(c => c.indicatorId === Number(indicatorId)) ?? contributions[0];
}

/**
 * `contributions[].buckets[]` -> legacy `componentItems[]`.
 *
 * One entry per bucket (life cycle stage / packaging level / component / ...),
 * with the scenario values joined in `scenarios[]` order. `componentName` holds
 * the scenario names because that is what the bar chart reads for its x-axis
 * categories — the legacy field name is a misnomer that predates this change.
 */
export function toLegacyComponentItems(
  contribution: ChartReportContribution | null,
  scenarios: ChartReportScenario[]
): any[] {
  if (!contribution?.buckets?.length) return [];

  const order = scenarioOrder(scenarios);
  const columnNames = (scenarios ?? []).map(s => s.shortName || s.name).join(';');

  // Group by bucket, preserving the order the engine emitted them in — that
  // order is the stacking order of the chart.
  const byBucket = new Map<string, { name: string; values: Map<number, any> }>();
  for (const bucket of contribution.buckets) {
    const key = bucket.bucketKey ?? bucket.bucketName;
    let entry = byBucket.get(key);
    if (!entry) {
      entry = { name: bucket.bucketName, values: new Map() };
      byBucket.set(key, entry);
    }
    entry.values.set(bucket.scenarioId, bucket);
  }

  return Array.from(byBucket.values()).map(entry => ({
    name: entry.name,
    componentName: columnNames,
    indicators: contribution.indicatorName,
    indicatorUnitName: contribution.indicatorUnitName,
    impactValues: order.map(id => formatImpactValue(entry.values.get(id)?.value)).join(';'),
    percentage: order.map(id => formatPercentage(entry.values.get(id)?.percentage)).join(';'),
  }));
}

/**
 * `scenarioTotals[].normalizedValue` + `indicators[]` -> legacy `scenarioList[]`.
 *
 * One entry per scenario; `totals` are the 0-1 radar axis values in indicator
 * order. Raw totals and saving percentages ride along under their legacy names
 * so anything reading them keeps working.
 */
export function toLegacySpiderScenarioList(data: GenerateChartReportData): any[] {
  const scenarios = data?.scenarios ?? [];
  const indicators = data?.indicators ?? [];
  const totals = data?.scenarioTotals ?? [];
  if (!scenarios.length || !indicators.length || !totals.length) return [];

  // (scenarioId, indicatorId) -> total, so each axis value is looked up by key
  // rather than by position in a split string.
  const byKey = new Map<string, any>();
  for (const total of totals) {
    byKey.set(`${total.scenarioId}|${total.indicatorId}`, total);
  }

  const indicatorNames = indicators.map(i => i.name).join(';');
  const spiderIcons = indicators.map(i => i.spiderIconUrl).join(';');
  const tableIcons = indicators.map(i => i.tableIconUrl).join(';');
  const units = indicators.map(i => i.unit).join(';');
  const equivalencyImages = indicators.map(i => i.equivalencyImageUrl).join(';');
  const equivalencyUnits = indicators.map(i => i.equivalencyText).join(';');
  const multipliers = indicators.map(i => i.multiplier).join(';');

  return scenarios.map(scenario => {
    const cells = indicators.map(i => byKey.get(`${scenario.scenarioId}|${i.id}`));
    return {
      scenarioId: scenario.scenarioId,
      name: scenario.name,
      scenarioname: scenario.name,
      shortScenarioName: scenario.shortName,
      numberofUnits: scenario.numberOfUnits,
      indicators: indicatorNames,
      indicatorImageName: spiderIcons,
      indicatorImageNameTable: tableIcons,
      indicatorUnitName: units,
      eqivalencyImage: equivalencyImages,
      equivalencyImageTable: equivalencyImages,
      indicatorEquivalancyUnits: equivalencyUnits,
      multiplier: multipliers,
      totals: cells.map(c => (c?.normalizedValue ?? 0).toString()).join(';'),
      totalScenarioImpact: cells
        .map(c => c?.formattedTotal ?? formatImpactValue(c?.total))
        .join(';'),
      savingPercentage: cells.map(c => c?.savingPercentage ?? 0).join(';'),
    };
  });
}

/**
 * `pef.normalized[]` / `pef.weighted[]` -> legacy `pef_normalized` /
 * `pef_weighted`.
 *
 * Both arrive as the same flat scenario x indicator list, but the two views
 * pivot it in opposite directions — matching how legacy grouped each one, and
 * therefore how the bar chart still reads them:
 *
 *   'scenario'  (Normalized) one row per scenario, axis = PEF categories.
 *               Grouped vertical columns, one series per scenario.
 *   'indicator' (Weighted)   one row per PEF category, axis = scenarios.
 *               Stacked horizontal bars, one series per category.
 *
 * The axis names go in the field the chart reads for that mode: `indicators`
 * when `isChartNormalized`, `itemName` / `componentName` otherwise.
 */
export function toLegacyPEFCategoryItems(
  rows: ChartReportPefValue[] | undefined,
  groupBy: 'scenario' | 'indicator'
): any[] {
  if (!rows?.length) return [];

  // First-seen order for both dimensions — that is the order the engine
  // emitted them in, and it is what the legacy `;`-joined strings preserved.
  const scenarioIds: number[] = [];
  const scenarioNames = new Map<number, string>();
  const indicatorIds: number[] = [];
  const indicatorNames = new Map<number, string>();
  const byKey = new Map<string, ChartReportPefValue>();

  for (const row of rows) {
    if (!scenarioNames.has(row.scenarioId)) {
      scenarioNames.set(row.scenarioId, row.scenarioName);
      scenarioIds.push(row.scenarioId);
    }
    if (!indicatorNames.has(row.indicatorId)) {
      indicatorNames.set(row.indicatorId, row.indicatorName);
      indicatorIds.push(row.indicatorId);
    }
    byKey.set(`${row.scenarioId}|${row.indicatorId}`, row);
  }

  const cellsFor = (scenarioId: number, indicatorId: number) =>
    byKey.get(`${scenarioId}|${indicatorId}`);

  const project = (cells: Array<ChartReportPefValue | undefined>) => ({
    impactValues: cells.map(c => c?.formattedValue ?? formatImpactValue(c?.value)).join(';'),
    percentage: cells.map(c => c?.formattedPercentage ?? formatPercentage(c?.percentage)).join(';'),
    isShowPercentage: true,
  });

  if (groupBy === 'indicator') {
    const axis = scenarioIds.map(id => scenarioNames.get(id)).join(';');
    return indicatorIds.map(indicatorId => ({
      indicatorId,
      name: indicatorNames.get(indicatorId),
      itemName: axis,
      componentName: axis,
      ...project(scenarioIds.map(scenarioId => cellsFor(scenarioId, indicatorId))),
    }));
  }

  const axis = indicatorIds.map(id => indicatorNames.get(id)).join(';');
  return scenarioIds.map(scenarioId => ({
    scenarioId,
    name: scenarioNames.get(scenarioId),
    indicators: axis,
    itemName: axis,
    componentName: axis,
    ...project(indicatorIds.map(indicatorId => cellsFor(scenarioId, indicatorId))),
  }));
}

/**
 * `packagingMetrics.rows[]` -> legacy `packagingComponentItems[]`.
 *
 * The gauge groups by `componentId` and then indexes into each group by the
 * scenario's position, so rows are emitted gauge-row-major and, within a row,
 * in `scenarios[]` order. Scenarios with no row for a metric still get a zero
 * entry, otherwise the positional index would slip.
 */
export function toLegacyPackagingComponentItems(data: GenerateChartReportData): any[] {
  const rows = data?.packagingMetrics?.rows ?? [];
  const scenarios = data?.scenarios ?? [];
  if (!rows.length || !scenarios.length) return [];

  const byKey = new Map<string, any>();
  const gaugeRows: number[] = [];
  const labels = new Map<number, string>();
  for (const row of rows) {
    byKey.set(`${row.gaugeRow}|${row.scenarioId}`, row);
    if (!labels.has(row.gaugeRow)) {
      labels.set(row.gaugeRow, row.label);
      gaugeRows.push(row.gaugeRow);
    }
  }
  gaugeRows.sort((a, b) => a - b);

  const items: any[] = [];
  for (const gaugeRow of gaugeRows) {
    for (const scenario of scenarios) {
      const row = byKey.get(`${gaugeRow}|${scenario.scenarioId}`);
      items.push({
        componentId: gaugeRow,
        componentName: labels.get(gaugeRow) ?? '',
        scenarioId: scenario.scenarioId,
        value: row?.value ?? 0,
        formatedValue: row?.formattedValue ?? `${row?.value ?? 0}`,
        isPercentage: row?.isPercentage ?? true,
      });
    }
  }
  return items;
}

/** `scenarios[]` -> the shape the gauge legend labels read. */
export function toLegacyScenarioData(scenarios: ChartReportScenario[]): any[] {
  return (scenarios ?? []).map(s => ({
    scenarioId: s.scenarioId,
    name: s.name,
    shortScenarioName: s.shortName,
    numberofUnits: s.numberOfUnits,
  }));
}
